// Everything that travels between the browser and the world.
//
// All of it is JSON. The old protocol had a hand-rolled binary snapshot codec
// because it carried bosses, projectiles, traps, pickups and per-class
// cooldowns twenty times a second; this one carries a handful of boxes with
// four numbers each, and a codec would be a second definition of the same
// shapes for nobody's benefit.

export interface ChatLineWire {
  id: number;
  from: string;
  text: string;
  kind: "say" | "system";
}

/** Socket closures that are final until the player explicitly tries again. */
export const SOCKET_CLOSE = {
  superseded: 4001,
  characterGone: 4003,
  full: 4004,
} as const;

/** Every answer the one shop action can give. Kept on the wire because both
 *  the simulation that decides it and the client that explains it need the
 *  same exhaustive vocabulary. */
export type ShopResult = "bought" | "too-far" | "too-poor" | "already-full";

export type ServerMessage =
  | {
      t: "welcome";
      selfId: number;
      slug: string;
      buildVersion: string;
      chat: ChatLineWire[];
      /** The maintenance message in force, or null when the game is open. */
      maintenance: string | null;
    }
  /** The maintenance lock started (a message) or ended (null). While it is in
   *  force the world applies no action from any client. */
  | { t: "maintenance"; message: string | null }
  | { t: "chat"; line: ChatLineWire }
  | { t: "snapshot"; snap: Snapshot }
  | { t: "shop"; result: ShopResult }
  | { t: "full"; online: number; cap: number }
  | { t: "error"; reason: string };

export type ClientMessage =
  | { t: "input"; mx: number; my: number }
  | { t: "attack" }
  | { t: "buy" }
  | { t: "chat"; text: string };

/** Read one untrusted client frame without letting a TypeScript assertion
 *  stand in for runtime validation. A parsed JSON value can still be null, an
 *  array, or an object with the wrong fields. */
export function readClientMessage(value: unknown): ClientMessage | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const message = value as Record<string, unknown>;
  switch (message.t) {
    case "input":
      if (!Number.isFinite(message.mx) || !Number.isFinite(message.my)) return undefined;
      return { t: "input", mx: message.mx as number, my: message.my as number };
    case "attack":
      return { t: "attack" };
    case "buy":
      return { t: "buy" };
    case "chat":
      return typeof message.text === "string" ? { t: "chat", text: message.text } : undefined;
    default:
      return undefined;
  }
}

/** One visible body. Every player is called "player", so the slug is the only
 *  field that distinguishes two of them. */
export interface PlayerView {
  id: number;
  /** Present on the frame a body enters view, and on your own body always.
   *
   *  A slug never changes, so re-sending six characters and their key twenty
   *  times a second for every body on screen was about a seventh of the whole
   *  snapshot spent restating something the client already knew. The server
   *  knows exactly which tick a body becomes visible — that is what the
   *  area-of-interest state is — so it says the slug once, then stops.
   *
   *  A client must therefore remember it: `SnapshotView` keeps the cache, and
   *  puts the slug back on every body before anything draws one. Nothing
   *  downstream of that sees it missing. */
  slug?: string;
  x: number;
  y: number;
  /** Visible and attack direction. */
  dir: number;
  /** Lane direction, used to smooth a turn without cutting the corner. */
  moveDir: number;
  hp: number;
  /** Seconds of swing left, so the client can draw the sword arc. */
  swing: number;
  /** Whether the body is walking, so the client can bob it. Sent rather than
   *  derived, because deriving it needs last frame's position and the client
   *  does not always have one — a body that has just streamed in has no
   *  history at all. */
  moving: boolean;
  /** True while the body is waiting to respawn. */
  dead: boolean;
}

export interface EnemyView {
  id: number;
  x: number;
  y: number;
  dir: number;
  hp: number;
  /** True for a moment after it is cut, so the client can flash it. */
  hurt: boolean;
}

/** Something that happened this tick, worth a spark. See `EventKind`. */
export interface EventView {
  k: number;
  x: number;
  y: number;
}

/** The bits of your own body nobody else is told about. */
export interface SelfView {
  hp: number;
  maxHp: number;
  gold: number;
  /** Whether you are standing close enough to the keeper to buy. */
  atShop: boolean;
  /** Seconds until the sword is ready, and until you are back on your feet. */
  cooldown: number;
  respawn: number;
}

export interface Snapshot {
  tick: number;
  selfId: number;
  self: SelfView;
  online: number;
  players: PlayerView[];
  enemies: EnemyView[];
  events: EventView[];
}
